import { Button } from "@jhool-io/fe-components";
import { useSearchParams } from "react-router-dom";
import { isMatch } from "date-fns";

import {
    appendMidnightTimeToDate,
    cn,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../utils/helpers";

interface TableFiltersDisplayProps {
    /** list of applied filters - key and value(s) */
    appliedFilters: {
        key: string;
        values: string[] | string;
        label?: string;
        preserveValues?: boolean;
        clearValue?: string;
    }[];
    /** wrapper class */
    wrapperClass?: string;

    // the key of local storage item to clear
    localStorageKey?: string;
}

export default function TableFiltersDisplay({
    appliedFilters,
    wrapperClass,
    localStorageKey,
}: TableFiltersDisplayProps) {
    const [searchParams, setSearchParams] = useSearchParams();

    const clearFilterParam = (filterKey: string, clearValue?: string) => {
        if (clearValue) searchParams.set(filterKey, clearValue);
        else searchParams.delete(filterKey);
    };

    const handleClearAllFilters = () => {
        appliedFilters.forEach((filter) => {
            clearFilterParam(filter.key, filter.clearValue);
        });
        setSearchParams(searchParams);
        if (localStorageKey) {
            localStorage.setItem(localStorageKey, searchParams.toString());
        }
    };

    const clearSingleFilter = (filterKey: string, clearValue?: string) => {
        clearFilterParam(filterKey, clearValue);
        setSearchParams(searchParams);
        if (localStorageKey) {
            localStorage.setItem(localStorageKey, searchParams.toString());
        }
    };

    const filterValueToDisplay = (
        val: string | string[],
        preserveValues?: boolean
    ) => {
        if (Array.isArray(val)) {
            if (preserveValues) {
                return val.join(", ");
            }

            return val
                .map((item) =>
                    makeStringFirstLetterCapital(item.replaceAll("_", " "))
                )
                .join(", ");
        }
        if (preserveValues) return val;
        if (typeof val === "string" && isMatch(val, "yyyy-MM-dd")) {
            return formatDate(appendMidnightTimeToDate(val), true);
        }
        return makeStringFirstLetterCapital(removeEnumUnderscore(val));
    };

    if (
        appliedFilters.filter(
            (filter) => filter.values && filter.values.length > 0
        ).length === 0
    )
        return null;

    return (
        <div className={cn("flex flex-col gap-y-1", wrapperClass)}>
            <div className="flex items-center ">
                <span className="text-base font-medium pr-2 [border-right:1px_solid_var(--color-stroke)] text-text">
                    Filters
                </span>
                <Button
                    size="auto"
                    variant="normal"
                    className="link"
                    onClick={handleClearAllFilters}
                >
                    Clear all
                </Button>
            </div>
            <div className="flex items-center gap-[--spacing-items] flex-wrap">
                {appliedFilters
                    .filter((filter) =>
                        typeof filter.values === "string"
                            ? Boolean(filter.values)
                            : filter.values.length !== 0
                    )
                    .map((filter) => {
                        return (
                            <div
                                className="px-2 py-0.5 rounded-md bg-[rgba(204,250,233,0.50)] w-auto inline-flex items-center gap-x-1 text-caption"
                                key={filter.key}
                            >
                                <span className="text-[#00563E]  tracking-[0.36px] font-medium">
                                    {`${
                                        filter.label ||
                                        makeStringFirstLetterCapital(
                                            removeEnumUnderscore(filter.key)
                                        ).replace("[]", "")
                                    }:`}
                                </span>
                                <div className="text-dark font-medium">
                                    {filterValueToDisplay(
                                        filter.values,
                                        filter.preserveValues
                                    )}
                                </div>
                                <Button
                                    size="auto"
                                    variant="normal"
                                    className="size-4 flex items-center justify-center"
                                    onClick={() => {
                                        clearSingleFilter(
                                            filter.key,
                                            filter.clearValue
                                        );
                                    }}
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="shrink-0 size-3.5 stroke-dark"
                                    >
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </Button>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
}
